<template>
  <div class="TravelProfiles">
    <div class="TravelProfiles-inner">
      <header class="TravelProfiles-header">
        <Eyebrow v-if="eyebrow" :label="eyebrow" />

        <h2 class="TravelProfiles-title">
          {{ title }}
        </h2>

        <p v-if="description" class="TravelProfiles-description">
          {{ description }}
        </p>
      </header>

      <ScrollCarousel :items="profiles" :ariaLabel="title">
        <li
          v-for="(profile, index) of profiles"
          :key="profile.title"
          class="TravelProfiles-card"
        >
          <CardTravelerPersona
            v-bind="profile"
            @click="emit('profile-selected', index)"
          />
        </li>
      </ScrollCarousel>

      <div v-if="action" class="TravelProfiles-action">
        <ActionButton
          size="lg"
          rightIcon="arrow-right"
          flipRtl
          @click="emit('action-click')"
        >
          {{ action }}
        </ActionButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Eyebrow from '@cvo/ui/components/atoms/Eyebrow/Eyebrow.vue';
import ActionButton from '@cvo/ui/components/atoms/ActionButton/ActionButton.vue';
import CardTravelerPersona from '@cvo/ui/components/domain/CardTravelerPersona/CardTravelerPersona.vue';
import ScrollCarousel from '@cvo/ui/components/molecules/ScrollCarousel/ScrollCarousel.vue';
import type { TravelProfilesProps, TravelProfilesEmits } from './TravelProfiles.model';

const props = defineProps<TravelProfilesProps>();
const emit = defineEmits<TravelProfilesEmits>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .TravelProfiles {
    &-inner {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-6;

      @include responsive.desktop {
        gap: vars.$dimensions-d-12;
      }
    }

    &-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: vars.$dimensions-d-2;
      text-align: center;

      @include responsive.tablet {
        gap: vars.$dimensions-d-4;
      }
    }

    &-title {
      margin: 0;
      color: vars.$patterns-travel-profiles-title-color;
      margin-block-start: vars.$dimensions-d-2;

      @include responsive.tablet {
        margin-block-start: vars.$dimensions-d-0;
      }

      @include text.heading-responsive('h4', 'h1', 'h1');
    }

    &-description {
      margin: 0;
      color: vars.$patterns-travel-profiles-description-color;

      @include text.text-responsive('md', 'lg', 'lg');
    }

    &-card {
      flex: 0 0 100%;
      scroll-snap-align: start;

      @include responsive.tablet {
        flex: 0 0 272px;
      }

      @include responsive.desktop {
        flex: 0 1 380px;
      }

      > * {
        height: 100%;
      }
    }

    &-action {
      display: flex;
      justify-content: center;
    }
  }
}
</style>
